import type { OrderStatus } from '@/bindings/OrderStatus'
import { parseOrderStatus } from '@/features/ordering/order-rules'
import { parsePageSearch } from '@/lib/page-search'

export type StatisticsTab = 'date' | 'month' | 'year' | 'range'

export const STATISTICS_TABS: readonly StatisticsTab[] = ['date', 'month', 'year', 'range']

/**
 * The statistics page's URL state (H5): the tab and what it was pointed at. Absent or malformed
 * values fall back to the tab's default (today, this month, this year, the last 7 days) at render.
 */
export interface StatisticsSearch {
  tab: StatisticsTab
  date?: string
  month?: string
  year?: string
  from?: string
  to?: string
}

/** Inclusive working dates, `YYYY-MM-DD` both. */
export interface Span {
  from: string
  to: string
}

const DATE = /^\d{4}-\d{2}-\d{2}$/
const MONTH = /^\d{4}-\d{2}$/
const YEAR = /^\d{4}$/

export function isIsoDate(value: unknown): value is string {
  if (typeof value !== 'string' || !DATE.test(value)) return false
  const parsed = new Date(`${value}T00:00:00Z`)
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().startsWith(value)
}

export function isMonth(value: unknown): value is string {
  return typeof value === 'string' && MONTH.test(value) && isIsoDate(`${value}-01`)
}

function isYear(value: unknown): value is string {
  return typeof value === 'string' && YEAR.test(value)
}

export function parseStatisticsSearch(search: Record<string, unknown>): StatisticsSearch {
  const tab = STATISTICS_TABS.find((candidate) => candidate === search.tab) ?? 'date'
  const out: StatisticsSearch = { tab }
  if (isIsoDate(search.date)) out.date = search.date
  if (isMonth(search.month)) out.month = search.month
  if (isYear(search.year)) out.year = search.year
  if (isIsoDate(search.from)) out.from = search.from
  if (isIsoDate(search.to)) out.to = search.to
  return out
}

/** The platform restaurant page's URL state: the month its order counts show, absent = this month. */
export interface MonthSearch {
  month?: string
}

export function parseMonthSearch(search: Record<string, unknown>): MonthSearch {
  return isMonth(search.month) ? { month: search.month } : {}
}

/** The bestsellers page's URL state: the span, and the tab it was opened from so the back link lands there. */
export interface SpanSearch extends Partial<Span> {
  tab?: StatisticsTab
}

export function parseSpanSearch(search: Record<string, unknown>): SpanSearch {
  const tab = STATISTICS_TABS.find((candidate) => candidate === search.tab)
  return {
    ...(isIsoDate(search.from) ? { from: search.from } : {}),
    ...(isIsoDate(search.to) ? { to: search.to } : {}),
    ...(tab ? { tab } : {}),
  }
}

/** The overview view that shows `span` under `tab`: the list pages' way back. */
export function viewOf(tab: StatisticsTab, span: Span): StatisticsSearch {
  switch (tab) {
    case 'date':
      return { tab, date: span.from }
    case 'month':
      return { tab, month: span.from.slice(0, 7) }
    case 'year':
      return { tab, year: span.from.slice(0, 4) }
    case 'range':
      return { tab, from: span.from, to: span.to }
  }
}

/** The orders-of-a-day page's URL state: the date, one status to show (absent = all), the page. */
export interface DateListSearch {
  date?: string
  status?: OrderStatus
  page: number
}

export function parseDateListSearch(search: Record<string, unknown>): DateListSearch {
  const status = parseOrderStatus(search.status)
  return {
    ...(isIsoDate(search.date) ? { date: search.date } : {}),
    ...(status ? { status } : {}),
    ...parsePageSearch(search),
  }
}

// -- date arithmetic on `YYYY-MM-DD` strings, in UTC so no zone can shift a day --

function toIso(date: Date): string {
  return date.toISOString().slice(0, 10)
}

export function addDays(date: string, days: number): string {
  const at = new Date(`${date}T00:00:00Z`)
  at.setUTCDate(at.getUTCDate() + days)
  return toIso(at)
}

export function monthSpan(month: string): Span {
  const [year, m] = month.split('-').map(Number) as [number, number]
  return { from: `${month}-01`, to: toIso(new Date(Date.UTC(year, m, 0))) }
}

function yearSpan(year: string): Span {
  return { from: `${year}-01-01`, to: `${year}-12-31` }
}

/** The tab's value with its default filled in from the branch's today. */
export function pointOf(search: StatisticsSearch, today: string): { date: string; month: string; year: string; range: Span } {
  const from = search.from ?? addDays(search.to ?? today, -6)
  const to = search.to ?? today
  return {
    date: search.date ?? today,
    month: search.month ?? today.slice(0, 7),
    year: search.year ?? today.slice(0, 4),
    range: from <= to ? { from, to } : { from: to, to: from },
  }
}

/** What the tab asks the backend for. */
export function spanOf(search: StatisticsSearch, today: string): Span {
  const point = pointOf(search, today)
  switch (search.tab) {
    case 'date':
      return { from: point.date, to: point.date }
    case 'month':
      return monthSpan(point.month)
    case 'year':
      return yearSpan(point.year)
    case 'range':
      return point.range
  }
}
